/**
 * @fileoverview Data Layer (DL) for Personal Quick Reaction ("Reacción Rápida Personal" - Tile ID 45).
 * Encapsulates all REST HTTP network requests to communicate with the .NET 8 backend controllers:
 *   - Public Fan Mobile & Overlay: `PersonalQuickReactionController.cs` (`/api/v1/personal-quick-reaction/`)
 *   - Admin Panel & Asset Uploads: `AdminPersonalQuickReactionController.cs` (`/api/v1/personal-quick-reaction/admin/`)
 * 
 * @module utilities/DL/PersonalQuickReaction
 */

/**
 * Returns the normalized base API URL ensuring a trailing slash.
 * @returns {string} Normalized API base URL.
 */
const getBaseUrl = () => {
    const raw = process.env.REACT_APP_API_URL || '/api/';
    return raw.endsWith('/') ? raw : `${raw}/`;
};

/**
 * Endpoint prefix for Personal Quick Reaction dynamic.
 * @constant {string}
 */
const BASE_URL = `${getBaseUrl()}v1/personal-quick-reaction`;

// =============================================================================
// PUBLIC & MOBILE FAN ENDPOINTS
// =============================================================================

/**
 * Retrieves the active game configuration and visual theme for a given room.
 * 
 * @async
 * @function GetConfig
 * @param {string} roomId - Unique identifier (GUID) of the room.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const GetConfig = async (roomId) => {
    try {
        const queryParam = roomId ? `?roomId=${encodeURIComponent(roomId)}` : '';
        const fetchResponse = await fetch(`${BASE_URL}/config${queryParam}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al obtener la configuración del juego.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Configuración obtenida exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.GetConfig:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al obtener configuración.' };
    }
};

export const GetPublicConfig = GetConfig;

/**
 * Registers a new participant, validates uniqueness, and generates a 256-bit One-Time Token (OTT).
 * 
 * @async
 * @function RegisterParticipant
 * @param {Object} payload - Registration payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response.
 */
export const RegisterParticipant = async (payload) => {
    try {
        const fetchResponse = await fetch(`${BASE_URL}/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            let errorMsg = responsePayload.message;
            if (!errorMsg && responsePayload.errors) {
                const errorList = Object.values(responsePayload.errors).flat();
                if (errorList.length > 0) errorMsg = errorList.join(', ');
            }
            return {
                success: false,
                message: errorMsg || 'Error al registrar al participante.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Participante registrado exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.RegisterParticipant:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al registrar participante.' };
    }
};

/**
 * Submits the reaction time, atomically consumes the OTT, evaluates anti-cheat thresholds,
 * calculates badges and persists the round.
 * 
 * @async
 * @function SubmitPlay
 * @param {Object} payload - Match completion payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response.
 */
export const SubmitPlay = async (payload) => {
    try {
        const fetchResponse = await fetch(`${BASE_URL}/submit-play`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al procesar y guardar la jugada.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Jugada registrada exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.SubmitPlay:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al registrar la jugada.' };
    }
};

/**
 * Tracks an individual milestone step in the 7-stage conversion funnel for the room.
 * 
 * @async
 * @function TrackTelemetry
 * @param {Object} payload - Telemetry event data.
 * @returns {Promise<{success: boolean, message?: string}>} Telemetry status.
 */
export const TrackTelemetry = async (payload) => {
    try {
        const fetchResponse = await fetch(`${BASE_URL}/telemetry`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return { success: false, message: responsePayload.message || 'Error al registrar telemetría.' };
        }
        return { success: true, message: responsePayload.message || 'Telemetría registrada.' };
    } catch (networkError) {
        console.warn('Advertencia en DL PersonalQuickReaction.TrackTelemetry:', networkError);
        return { success: false, message: networkError.message };
    }
};

/**
 * Retrieves the official room leaderboard ranking ordered by best reaction times.
 * 
 * @async
 * @function GetRanking
 * @param {string} roomId - Room GUID.
 * @param {number} [limit=10] - Maximum count of top participants to return.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Leaderboard ranking.
 */
export const GetRanking = async (roomId, limit = 10) => {
    try {
        const queryParams = new URLSearchParams();
        if (roomId) queryParams.append('roomId', roomId);
        queryParams.append('limit', String(limit || 10));

        const fetchResponse = await fetch(`${BASE_URL}/ranking?${queryParams.toString()}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al obtener la tabla de clasificación.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Ranking obtenido exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.GetRanking:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al obtener el ranking.' };
    }
};

/**
 * Retrieves the current room record holder / winner for the Live Broadcast Overlay.
 * 
 * @async
 * @function GetWinner
 * @param {string} roomId - Room GUID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Winner details.
 */
export const GetWinner = async (roomId) => {
    try {
        const queryParam = roomId ? `?roomId=${encodeURIComponent(roomId)}` : '';
        const fetchResponse = await fetch(`${BASE_URL}/winner${queryParam}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al obtener el ganador de la sala.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Ganador obtenido exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.GetWinner:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al obtener el ganador.' };
    }
};

// =============================================================================
// ADMINISTRATIVE & DASHBOARD ENDPOINTS
// =============================================================================

/**
 * Retrieves complete administration snapshot (configs, participants, funnel KPIs, audit logs and themes).
 * 
 * @async
 * @function GetAdminData
 * @param {string} roomId - Room GUID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Administrative snapshot.
 */
export const GetAdminData = async (roomId) => {
    try {
        const queryParam = roomId ? `?roomId=${encodeURIComponent(roomId)}` : '';
        const fetchResponse = await fetch(`${BASE_URL}/admin/data${queryParam}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al obtener datos administrativos.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Datos de administración cargados exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.GetAdminData:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al obtener datos de administración.' };
    }
};

/**
 * Saves game configuration.
 * 
 * @async
 * @function SaveConfig
 * @param {Object} payload - Configuration payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Save outcome.
 */
export const SaveConfig = async (payload) => {
    try {
        const fetchResponse = await fetch(`${BASE_URL}/admin/config`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al guardar la configuración del juego.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Configuración guardada exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.SaveConfig:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al guardar configuración.' };
    }
};

export const UpdateAdminConfig = SaveConfig;

/**
 * Updates Live Overlay operating configuration.
 * 
 * @async
 * @function SaveOverlayConfig
 * @param {Object} payload - Overlay configuration payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Save outcome.
 */
export const SaveOverlayConfig = async (payload) => {
    try {
        const fetchResponse = await fetch(`${BASE_URL}/admin/overlay`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al actualizar el overlay.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Overlay actualizado exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.SaveOverlayConfig:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al actualizar overlay.' };
    }
};

export const UpdateAdminOverlay = SaveOverlayConfig;

/**
 * Retrieves themes list for a room.
 * 
 * @async
 * @function GetThemes
 * @param {string} roomId - Room GUID.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Themes list.
 */
export const GetThemes = async (roomId) => {
    try {
        const queryParam = roomId ? `?roomId=${encodeURIComponent(roomId)}` : '';
        const fetchResponse = await fetch(`${BASE_URL}/admin/themes${queryParam}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al obtener los temas visuales.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Temas obtenidos exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.GetThemes:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al obtener temas.' };
    }
};

/**
 * Saves a visual theme.
 * 
 * @async
 * @function SaveTheme
 * @param {Object} payload - Theme payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Save outcome.
 */
export const SaveTheme = async (payload) => {
    try {
        const fetchResponse = await fetch(`${BASE_URL}/admin/theme`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al guardar el tema visual.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Tema guardado exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.SaveTheme:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al guardar tema.' };
    }
};

/**
 * Deletes a visual theme.
 * 
 * @async
 * @function DeleteTheme
 * @param {string} roomId - Room GUID.
 * @param {string} themeId - Theme GUID.
 * @returns {Promise<{success: boolean, message?: string}>} Deletion result.
 */
export const DeleteTheme = async (roomId, themeId) => {
    try {
        const fetchResponse = await fetch(`${BASE_URL}/admin/theme/${encodeURIComponent(themeId)}?roomId=${encodeURIComponent(roomId)}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al eliminar el tema visual.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            message: responsePayload.message || 'Tema eliminado exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.DeleteTheme:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al eliminar tema.' };
    }
};

/**
 * Grants a retry / soft-reset ("Dar otra oportunidad") to a participant.
 * 
 * @async
 * @function ResetUser
 * @param {Object} payload - Soft-reset payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Reset outcome with new OTT.
 */
export const ResetUser = async (payload) => {
    try {
        const fetchResponse = await fetch(`${BASE_URL}/admin/reset-user`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al conceder reintento al participante.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            message: responsePayload.message || 'Reintento concedido exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.ResetUser:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al procesar reintento.' };
    }
};

export const ResetParticipant = ResetUser;

/**
 * Purges all participants and telemetry for a room (Danger Zone) validating the master security password.
 * 
 * @async
 * @function FullReset
 * @param {Object} payload - Full reset payload.
 * @returns {Promise<{success: boolean, message?: string}>} Reset outcome.
 */
export const FullReset = async (payload) => {
    try {
        const fetchResponse = await fetch(`${BASE_URL}/admin/full-reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al purgar los registros de la sala.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            message: responsePayload.message || 'Sala purgada exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.FullReset:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al ejecutar reinicio total.' };
    }
};

/**
 * Uploads media assets (single or batch) to be processed and compressed via SkiaSharp on the backend.
 * 
 * @async
 * @function UploadAsset
 * @param {FormData} formData - Multipart form data containing file(s) and assetType metadata.
 * @param {string} [roomId] - Optional room GUID to attach to the query string.
 * @returns {Promise<{success: boolean, url?: string, urls?: Array<string>, message?: string}>} Upload result.
 */
export const UploadAsset = async (formData, roomId) => {
    try {
        const url = roomId ? `${BASE_URL}/admin/upload-asset?roomId=${encodeURIComponent(roomId)}` : `${BASE_URL}/admin/upload-asset`;
        
        const fetchResponse = await fetch(url, {
            method: 'POST',
            body: formData
        });

        const responsePayload = await fetchResponse.json().catch(() => ({}));
        if (!fetchResponse.ok) {
            return {
                success: false,
                message: responsePayload.message || 'Error al subir y optimizar el archivo multimedia.',
                data: responsePayload
            };
        }
        return {
            success: responsePayload.success !== false,
            data: responsePayload.data !== undefined ? responsePayload.data : responsePayload,
            url: responsePayload.url || responsePayload.data?.url,
            urls: responsePayload.urls || responsePayload.data?.urls,
            message: responsePayload.message || 'Archivo subido y procesado exitosamente.'
        };
    } catch (networkError) {
        console.error('Error en DL PersonalQuickReaction.UploadAsset:', networkError);
        return { success: false, message: networkError.message || 'Error de conexión al subir el archivo multimedia.' };
    }
};

const DLPersonalQuickReaction = {
    GetConfig,
    GetPublicConfig,
    RegisterParticipant,
    SubmitPlay,
    TrackTelemetry,
    GetRanking,
    GetWinner,
    GetAdminData,
    SaveConfig,
    UpdateAdminConfig,
    SaveOverlayConfig,
    UpdateAdminOverlay,
    GetThemes,
    SaveTheme,
    DeleteTheme,
    ResetUser,
    ResetParticipant,
    FullReset,
    UploadAsset
};

export default DLPersonalQuickReaction;
